/**
 * Screen-space ambient occlusion (D-047 lighting overhaul). A single-sample depth + view-normal prepass (opaque
 * geometry only, one override material) feeds three's GTAO node and its Poisson denoiser; the result is rendered
 * into `target` before the scene pass, which reads it through a builtin AO context. AO therefore multiplies only
 * indirect light: the baked-sky reflections and the SH fill probe (planetshine and nebula glow,
 * environment/fillProbe.ts). The star key, emissives and effects are never darkened (transparent materials skip the
 * context). AO fades out with distance once its world radius shrinks to a few pixels, and is off on Low.
 *
 * The prepass depth is also the depth the atmosphere effects read (the MSAA scene pass has no resolvable depth).
 *
 * Internal-pixel cap (G2, D-970/D-971): GTAO and its denoiser cost scales with pixels (~2.5 ms at 2160p High). Above
 * `maxPixels` they run at min(scale, sqrt(maxPixels / drawing pixels)) and a joint bilateral upsample (bilinear
 * weights x view-depth and normal agreement of each low-res tap against the full-res prepass) writes a full-res AO
 * target, so silhouettes don't halo. At or below the cap the path is exactly the old one (no upsample pass).
 */
import { DoubleSide, Matrix4, NodeMaterial, PassNode, QuadMesh, RenderTarget, UnsignedByteType, Vector2, type Camera, type DataTexture, type Node, type NodeFrame, type Scene, type TextureNode, type UniformNode, type WebGPURenderer } from 'three/webgpu'
import { abs, builtinAOContext, dot, exp, faceDirection, float, floor, Fn, getViewPosition, max, mix, normalView, positionView, pow, saturate, screenUV, smoothstep, texture, uniform, uv, vec2, vec4 } from 'three/tsl'
import { ao } from 'three/addons/tsl/display/GTAONode.js'
import { denoise } from 'three/addons/tsl/display/DenoiseNode.js'

export interface AoSettings {
  /** 0 = off. */
  samples: number
  /** GTAO resolution relative to the drawing buffer. */
  scale: number
  /** View-space radius (metres). */
  radius: number
  thickness: number
  strength: number
  /** Contrast exponent on the visibility (1 = GTAO as computed). */
  power: number
  /** AO fades from full at `fade[0]` metres to none at `fade[1]`. */
  fade: readonly [number, number]
  /** Cap on GTAO + denoise internal pixels (G2); above it they run smaller and are upsampled depth-aware. */
  maxPixels: number
  /** Upsample depth rejection sharpness (on the relative view-depth difference). */
  upsampleDepth: number
  /** Upsample normal-agreement exponent (0 = depth only). */
  upsampleNormal: number
}

/** The AO internal size for one drawing-buffer size (written into `out`; no allocation). */
export interface AoTargetSize {
  w: number
  h: number
  /** The GTAO resolution scale actually used. */
  scale: number
  /** True when the cap bound (scale < the preset's): the upsample pass runs. */
  capped: boolean
}

/**
 * The AO internal size: the preset's scale, or less when drawing pixels x scale^2 would exceed `maxPixels`. Below the
 * cap the result is exactly the uncapped one, so <= 1080p rendering is unchanged.
 */
export function aoTargetSize(drawW: number, drawH: number, scale: number, maxPixels: number, out: AoTargetSize): AoTargetSize {
  const pixels = drawW * drawH * scale * scale
  const s = pixels > maxPixels ? Math.sqrt(maxPixels / (drawW * drawH)) : scale
  out.scale = s
  out.capped = s < scale
  out.w = Math.max(1, Math.round(drawW * s))
  out.h = Math.max(1, Math.round(drawH * s))
  return out
}

export interface AoStage {
  /** Depth + normals prepass (depth is reversed-Z, view normals are raw -1..1). */
  readonly prePass: PassNode
  readonly depth: TextureNode
  /** Raw view normals of the prepass. */
  readonly normals: TextureNode
  /** The context the scene pass installs: AO on indirect light only. */
  readonly context: Node
  /** Renders prepass, GTAO and the denoiser into the AO target (call before the scene pass; no-op when off). */
  render(): void
  configure(s: AoSettings): void
  readonly enabled: boolean
  /** The denoised AO (debug views). */
  readonly aoTexture: TextureNode
  dispose(): void
}

type GtaoNode = ReturnType<typeof ao> & {
  resolutionScale: number
  radius: UniformNode<'float', number>
  thickness: UniformNode<'float', number>
  samples: UniformNode<'float', number>
  getTextureNode(): TextureNode
  setSize(width: number, height: number): void
  _resolutionScale: UniformNode<'float', number>
  dispose(): void
}

type BackgroundScene = Scene & { backgroundNode: Node | null }

/**
 * The depth + normals prepass, drawn without the scene background (gate-A landing, D-TBD). three draws the background
 * mesh with its own material in every pass (`allowOverride = false`), so the prepass drew it too. On WebGL2 the
 * prepass and the scene pass share one render context (both single-sample half-float targets), and three keys a
 * render object by object, material, context and lights; the background's object differed only in the context node
 * (the scene pass's AO context), so each pass disposed the other's and rebuilt it: two node builds and a shader relink
 * every frame, plus the garbage, which gave the long first-thrust frames of `flight.spec`. The prepass needs no
 * background: it writes no depth, and GTAO and the haze read only the prepass depth and the geometry's normals.
 */
export class PrePassNode extends PassNode {
  constructor(scene: Scene, camera: Camera) {
    super(PassNode.COLOR, scene, camera, { samples: 0 })
  }

  override updateBefore(frame: NodeFrame): undefined {
    const scene = this.scene as BackgroundScene
    const background = scene.backgroundNode
    scene.backgroundNode = null
    try {
      super.updateBefore(frame)
    } finally {
      scene.backgroundNode = background
    }
    return undefined
  }
}

export function createAoStage(renderer: WebGPURenderer, scene: Scene, camera: Camera, white: DataTexture): AoStage {
  // Opaque-only prepass with one override material: positions (including instancing and per-material position
  // nodes, which the renderer carries over to override materials) and the view normal; no background (PrePassNode).
  const normalMaterial = new NodeMaterial()
  normalMaterial.name = 'post.prepassNormal'
  normalMaterial.side = DoubleSide
  normalMaterial.fragmentNode = vec4(normalView.mul(faceDirection), 1)
  const prePass = new PrePassNode(scene, camera)
  prePass.transparent = false
  prePass.overrideMaterial = normalMaterial
  const depth = prePass.getTextureNode('depth')
  const normal = prePass.getTextureNode('output')

  const gtao = ao(depth, normal, camera) as unknown as GtaoNode
  // Below scale 1 three's GTAO takes the min of a 2x2 depth gather as the centre depth: the far depth under reversed Z,
  // which sits behind the surface and over-occludes every slanted panel (G2 parity: hull faces and hall floors went
  // visibly darker). Its uniform only drives that choice, so pin it at 1: the centre depth is the plain sample, as at
  // full resolution, and the upsample below reads the same sample.
  const gtaoSetSize = gtao.setSize.bind(gtao)
  gtao.setSize = (width: number, height: number): void => {
    gtaoSetSize(width, height)
    gtao._resolutionScale.value = 1
  }
  const gtaoTex = gtao.getTextureNode()
  const denoised = denoise(gtaoTex, depth, normal, camera) as unknown as Node<'vec4'>

  const target = new RenderTarget(1, 1, { depthBuffer: false, type: UnsignedByteType })
  target.texture.name = 'post.ao'
  const quadMaterial = new NodeMaterial()
  quadMaterial.name = 'post.aoDenoise'
  quadMaterial.fragmentNode = vec4(denoised.r, denoised.r, denoised.r, 1)
  const quad = new QuadMesh(quadMaterial)

  // Joint bilateral upsample of the capped AO to full resolution (G2). Each of the four low-res texels around the
  // pixel keeps its bilinear weight scaled by how well its prepass view depth and normal match the pixel's, so AO
  // from a hull never bleeds onto the background behind its silhouette (or the reverse). A small bilinear floor keeps
  // a pixel whose four taps all reject (thin features) from going to 0/0.
  const full = new RenderTarget(1, 1, { depthBuffer: false, type: UnsignedByteType })
  full.texture.name = 'post.aoFull'
  const lowSize = uniform(new Vector2(1, 1))
  const projInv = uniform(new Matrix4())
  const depthSharp = uniform(40)
  const normalPow = uniform(8)
  const lowAo = texture(target.texture)
  const upsampleNode = Fn(() => {
    const p = uv().mul(lowSize).sub(0.5)
    const base = floor(p)
    const f = p.sub(base)
    const zc = getViewPosition(uv(), depth.sample(uv()).r, projInv).z.toConst()
    const nc = normal.sample(uv()).xyz.toConst()
    const sum = float(0).toVar()
    const wsum = float(0).toVar()
    for (const [ox, oy] of [[0, 0], [1, 0], [0, 1], [1, 1]] as const) {
      const tuv = base.add(vec2(ox, oy)).add(0.5).div(lowSize).toConst()
      const bx = ox === 0 ? float(1).sub(f.x) : f.x
      const by = oy === 0 ? float(1).sub(f.y) : f.y
      const bilinear = bx.mul(by)
      // The tap's depth as GTAO saw it (the plain sample at the low-res texel centre; see the setSize pin above).
      const zk = getViewPosition(tuv, depth.sample(tuv).r, projInv).z
      const dz = abs(zk.sub(zc)).div(max(abs(zc), 1e-3))
      const wd = exp(dz.mul(depthSharp).negate())
      const wn = pow(saturate(dot(normal.sample(tuv).xyz, nc)), normalPow)
      const w = bilinear.mul(wd.mul(wn).add(1e-4))
      sum.addAssign(lowAo.sample(tuv).r.mul(w))
      wsum.addAssign(w)
    }
    const v = sum.div(max(wsum, 1e-6))
    return vec4(v, v, v, 1)
  })
  const upsampleMaterial = new NodeMaterial()
  upsampleMaterial.name = 'post.aoUpsample'
  upsampleMaterial.fragmentNode = upsampleNode()
  const upsampleQuad = new QuadMesh(upsampleMaterial)

  // The scene pass samples the AO target (a plain texture: its pipeline never changes with the preset).
  const aoTex = texture(white)
  const strength = uniform(0)
  const power = uniform(1)
  const fadeNear = uniform(150)
  const fadeFar = uniform(600)
  const distanceFade = float(1).sub(smoothstep(fadeNear, fadeFar, positionView.z.negate()))
  const occlusion = mix(float(1), pow(aoTex.sample(screenUV).r, power), strength.mul(distanceFade))
  const context = builtinAOContext(occlusion)

  let enabled = false
  let scale = 1
  let maxPixels = Number.POSITIVE_INFINITY
  const size = { w: 0, h: 0 }
  const fullSize = { w: 0, h: 0 }
  const sized: AoTargetSize = { w: 1, h: 1, scale: 1, capped: false }
  const drawing = new Vector2()

  const stage: AoStage = {
    prePass,
    depth,
    normals: normal,
    context,
    aoTexture: aoTex,
    get enabled(): boolean {
      return enabled
    },
    configure(s: AoSettings): void {
      enabled = s.samples > 0
      gtao.samples.value = Math.max(s.samples, 4)
      scale = s.scale
      maxPixels = s.maxPixels
      depthSharp.value = s.upsampleDepth
      normalPow.value = s.upsampleNormal
      gtao.resolutionScale = s.scale
      gtao.radius.value = s.radius
      gtao.thickness.value = s.thickness
      strength.value = enabled ? s.strength : 0
      power.value = s.power
      fadeNear.value = s.fade[0]
      fadeFar.value = s.fade[1]
      aoTex.value = enabled ? target.texture : white
    },
    render(): void {
      if (!enabled) return
      renderer.getDrawingBufferSize(drawing)
      aoTargetSize(drawing.x, drawing.y, scale, maxPixels, sized)
      gtao.resolutionScale = sized.scale
      if (sized.w !== size.w || sized.h !== size.h) {
        target.setSize(sized.w, sized.h)
        size.w = sized.w
        size.h = sized.h
        lowSize.value.set(sized.w, sized.h)
      }
      const previous = renderer.getRenderTarget()
      renderer.setRenderTarget(target)
      quad.render(renderer)
      if (sized.capped) {
        const fw = Math.max(1, drawing.x)
        const fh = Math.max(1, drawing.y)
        if (fw !== fullSize.w || fh !== fullSize.h) {
          full.setSize(fw, fh)
          fullSize.w = fw
          fullSize.h = fh
        }
        projInv.value.copy(camera.projectionMatrixInverse)
        renderer.setRenderTarget(full)
        upsampleQuad.render(renderer)
        aoTex.value = full.texture
      } else {
        aoTex.value = target.texture
      }
      renderer.setRenderTarget(previous)
    },
    dispose(): void {
      gtao.dispose()
      prePass.dispose()
      target.dispose()
      full.dispose()
      quadMaterial.dispose()
      upsampleMaterial.dispose()
      normalMaterial.dispose()
    },
  }
  return stage
}
